/**
 * Draggable Overview Tab Component
 * Allows owners to customize their dashboard layout via drag-and-drop
 * Sections can be reordered and persist to backend API
 * Uses industry-specific and role-specific templates
 * 
 * V2: Enhanced with AI Intelligence Hero and Connected Sources
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/utils/safeToast';
import { logIfReal } from '@/utils/transientError';

import { LiveBenchmarkPanel } from '../LiveBenchmarkPanel';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  GripVertical, Settings, Eye, EyeOff, ChevronDown, ChevronUp,
  Trophy, AlertTriangle, Lightbulb, DollarSign, Users, TrendingUp,
  Target, Cloud, ListTodo, Activity, Link2, Sparkles, BarChart3,
  Lock, Unlock, LayoutGrid, Layers, Loader2, Check, Brain, Database, Flame
} from 'lucide-react';
import { INDUSTRY_CONFIG, getIndustryConfig } from '@/config/dashboardConfig';
import { getDashboardTemplate, getIndustryTemplate, INDUSTRY_TEMPLATES } from '@/config/dashboardTemplates';
import { InsightSummaryCard } from '../../insights/InsightEngineComponents';
import { PatternAwareInsightCard, SeasonalOutlookBanner } from '../../insights/PatternAwareInsights';
import { CreditsWidget } from '@/components/widgets/CreditsWidget';
import { UpsellTeasers } from '../UpsellTeasers';
import AISavingsFinderTile from '../AISavingsFinderTile';
import { SPLHComparisonDashboard } from '../SPLHComparisonDashboard';
import { LocationComparisonMode } from '../LocationComparisonMode';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/contexts/ClientContext';
import { getQSRDashboardMetrics, getQSRKpis, getQSRLiveMetrics, getQSRWins } from '@/config/qsrLocationMetrics';
import {
  WinsSection,
  ConcernsSection,
  BenchmarkAlertsSection,
  OpportunitiesSection,
  FinancialSection,
  PerformanceSection,
  MarketSection,
  PlanningSection,
  KPISection,
  LiveMetricsBar,
  TasksRecommendationsRow,
  QuickStatsGrid,
  QuickLinksSection,
  RecentActivitySection
} from './OverviewSections';
import EconomicIndicatorsWidget from '../EconomicIndicatorsWidget';
import CorvanaIntelligenceWidget from '../CorvanaIntelligenceWidget';
import AIIntelligenceHero from '../AIIntelligenceHero';
import ConnectedSourcesStrip from '../ConnectedSourcesStrip';
import { AINarrativeEngine } from '../../intelligence/AINarrativeEngine';
import { PredictiveAnomalyDetection } from '../../intelligence/PredictiveAnomalyDetection';
import { KnowledgeTransferEngine } from '../../intelligence/KnowledgeTransferEngine';
import { StaffROIScoring } from '../../intelligence/StaffROIScoring';
import { WhatIfScenarioModeler } from '../../intelligence/WhatIfScenarioModeler';
import { RevenueLeakageDetector } from '../../intelligence/RevenueLeakageDetector';
import { ExecutiveSummaryGenerator } from '../../intelligence/ExecutiveSummaryGenerator';
import { DemoDataGate } from './DemoDataGate';
import { CustomerExperienceScorecard } from '../../intelligence/CustomerExperienceScorecard';
import { ModuleTeaser } from '../../intelligence/ModuleTeaser';
import { ModuleTogglePanel } from '../../intelligence/ModuleTogglePanel';
import { isSectionUnlocked, getTeaserForSection, CORE_SECTIONS } from '@/config/moduleSectionMap';
import { API_URL } from '@/config/apiConfig';
import { authFetch } from '@/utils/authFetch';

// Default section order and configuration — Role-priority: key figures and wins first
const DEFAULT_SECTIONS = [{ id: 'insights', label: 'AI Intelligence & Greeting', icon: Brain, priority: 'critical', category: 'intelligence' }, { id: 'wins', label: 'Recent Wins', icon: Trophy, priority: 'high', category: 'performance' }, { id: 'kpis', label: 'KPI Dashboard', icon: BarChart3, priority: 'high', category: 'metrics' }, { id: 'financial', label: 'Financial Intelligence', icon: DollarSign, priority: 'critical', category: 'finance' }, { id: 'concerns', label: 'Business Concerns', icon: AlertTriangle, priority: 'critical', category: 'alerts' }, { id: 'splh-heatmap', label: 'SPLH Heatmap', icon: Flame, priority: 'high', category: 'qsr' }, { id: 'location-comparison', label: 'Location Comparison', icon: BarChart3, priority: 'high', category: 'qsr' }, { id: 'connected-sources', label: 'Connected Sources', icon: Database, priority: 'critical', category: 'intelligence' }, { id: 'module-control', label: 'Module Control', icon: Settings, priority: 'critical', category: 'intelligence' }, { id: 'seasonal', label: 'Seasonal Outlook', icon: Cloud, priority: 'high', category: 'intelligence' }, { id: 'benchmark-alerts', label: 'Benchmark Alerts', icon: BarChart3, priority: 'high', category: 'alerts' }, { id: 'live-benchmarks', label: 'Live Industry Benchmarks', icon: Database, priority: 'high', category: 'market' }, { id: 'opportunities', label: 'Opportunities', icon: Lightbulb, priority: 'high', category: 'growth' }, { id: 'economic-pulse', label: 'Australian Economic Pulse', icon: Activity, priority: 'high', category: 'market' }, { id: 'live-metrics', label: 'Live Metrics', icon: Activity, priority: 'medium', category: 'metrics' }, { id: 'tasks', label: 'Tasks & Recommendations', icon: ListTodo, priority: 'high', category: 'operations' }, { id: 'performance', label: 'Performance & Feedback', icon: Users, priority: 'medium', category: 'operations' }, { id: 'market', label: 'Market & Operations', icon: TrendingUp, priority: 'medium', category: 'market' }, { id: 'planning', label: 'Planning & Context', icon: Target, priority: 'medium', category: 'planning' }, { id: 'ai-narrative', label: 'Daily Intelligence Briefing', icon: Brain, priority: 'high', category: 'intelligence' }, { id: 'predictive-alerts', label: 'Predictive Alerts', icon: AlertTriangle, priority: 'high', category: 'alerts' }, { id: 'knowledge-transfer', label: 'Knowledge Transfer', icon: Sparkles, priority: 'medium', category: 'intelligence' }, { id: 'staff-roi', label: 'Staff Revenue Impact', icon: Users, priority: 'medium', category: 'performance' }, { id: 'what-if', label: 'What-If Scenarios', icon: Sparkles, priority: 'medium', category: 'intelligence' }, { id: 'revenue-leakage', label: 'Revenue Leakage', icon: AlertTriangle, priority: 'high', category: 'finance' }, { id: 'executive-summary', label: 'Executive Summary', icon: BarChart3, priority: 'medium', category: 'intelligence' }, { id: 'cx-scorecard', label: 'Customer Experience', icon: Users, priority: 'medium', category: 'performance' }, { id: 'upsell', label: 'Unlock More Value', icon: Lightbulb, priority: 'low', category: 'growth' }, { id: 'quick-stats', label: 'Quick Stats', icon: BarChart3, priority: 'low', category: 'metrics' }, { id: 'activity', label: 'Recent Activity', icon: Activity, priority: 'low', category: 'activity' }, { id: 'quick-links', label: 'Quick Links', icon: Link2, priority: 'low', category: 'navigation' }];

const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-amber-100 text-amber-700 border-amber-200',
  medium: 'bg-blue-100 text-blue-700 border-blue-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200'
};

const CATEGORY_COLORS = {
  intelligence: 'from-purple-500 to-indigo-500',
  alerts: 'from-red-500 to-orange-500',
  finance: 'from-emerald-500 to-[#0066CC]',
  performance: 'from-blue-500 to-[#007AFF]',
  growth: 'from-amber-500 to-yellow-500',
  market: 'from-pink-500 to-rose-500',
  planning: 'from-indigo-500 to-purple-500',
  metrics: 'from-slate-500 to-gray-500',
  operations: 'from-green-500 to-emerald-500',
  activity: 'from-[#007AFF] to-blue-500',
  navigation: 'from-gray-500 to-slate-500'
};

// Per-section collapse state, shared with every SortableSection instance via
// context so we don't have to thread props through ~30 call sites. The list of
// collapsed section IDs is persisted per-user via /api/dashboard/preferences.
const SectionCollapseContext = React.createContext({ collapsedList: [], toggle: () => {}, getSummary: () => null });

// Sortable Section Wrapper
const SortableSection = ({ id, children, isEditMode, section, onToggleVisibility, isVisible }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id, disabled: !isEditMode });

  const { collapsedList, toggle: toggleCollapse, getSummary } = React.useContext(SectionCollapseContext);
  const isCollapsed = collapsedList.includes(id);
  // Collapse is an everyday (non-edit) affordance — disabled while customising so
  // it never interferes with drag-to-reorder or show/hide.
  const showCollapsed = isCollapsed && !isEditMode;
  const SectionIcon = section?.icon;
  const summaryText = showCollapsed && getSummary ? getSummary(id) : null;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  if (!isVisible && !isEditMode) return null;

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {isEditMode && (
        <div className="absolute -left-2 top-0 bottom-0 flex items-start pt-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <div
            {...attributes}
            {...listeners}
            className="p-2 rounded-lg bg-white shadow-lg border cursor-grab active:cursor-grabbing hover:bg-slate-50"
          >
            <GripVertical className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      )}
      
      {isEditMode && (
        <div className="absolute -right-2 top-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1 p-1 rounded-lg bg-white shadow-lg border">
            <Badge className={`text-[10px] ${PRIORITY_COLORS[section.priority]}`}>
              {section.priority}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => onToggleVisibility(id)}
            >
              {isVisible ? (
                <Eye className="w-3 h-3 text-emerald-600" />
              ) : (
                <EyeOff className="w-3 h-3 text-slate-400" />
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Collapse toggle — floating chevron shown on hover when the card is expanded */}
      {!isEditMode && !showCollapsed && (
        <button
          type="button"
          onClick={() => toggleCollapse(id)}
          aria-label={`Collapse ${section?.label || 'section'}`}
          data-testid={`collapse-section-${id}`}
          className="absolute right-2 top-2 z-10 p-1.5 rounded-lg bg-background/80 backdrop-blur border border-border/60 shadow-sm text-muted-foreground hover:text-foreground hover:bg-background opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto transition-opacity"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
      )}

      {showCollapsed ? (
        <button
          type="button"
          onClick={() => toggleCollapse(id)}
          aria-label={`Expand ${section?.label || 'section'}`}
          data-testid={`expand-section-${id}`}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-border/60 bg-card/50 hover:bg-card transition-colors text-left"
        >
          <span className="flex items-center gap-2.5 min-w-0">
            {SectionIcon && <SectionIcon className="w-4 h-4 shrink-0 text-muted-foreground" />}
            <span className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-foreground truncate">{section?.label || 'Section'}</span>
              {summaryText && <span className="text-xs text-muted-foreground truncate">{summaryText}</span>}
            </span>
          </span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
            Show
            <ChevronDown className="w-4 h-4" />
          </span>
        </button>
      ) : (
        <div className={`${!isVisible ? 'opacity-40' : ''} ${isEditMode ? 'ring-2 ring-indigo-200 ring-offset-2 rounded-xl' : ''}`}>
          {children}
        </div>
      )}
    </div>
  );
};

// Main Component
export const DraggableOverviewTab = ({
  userName,
  businessId,
  businessName,
  industry,
  lastLogin,
  canSeeTeamView,
  unifiedMetrics,
  unifiedWins,
  unifiedOpportunities,
  summaryData,
  kpis,
  tasks,
  recommendations,
  liveMetrics,
  completedCount,
  pendingCount,
  highPriorityCount,
  isWidgetEnabled,
  onToggleTask,
  onExpandTask,
  onExpandCard,
  setActiveTab,
  setFilter,
  isDemoAccount = false,
  dataSources = null
}) => {
  const navigate = useNavigate();
  const { user} = useAuth();
  const { clientType, currentLocation, customerBusiness, enabledModules: contextModules, toggleModule } = useClient();
  const industryConfig = getIndustryConfig(industry);
  const isQSR = industry === 'qsr';
  
  // Get user's enabled modules — use context modules (live toggling) as source of truth
  const activeModules = contextModules || [];
  const industryData = user?.industry_data || {};
  
  // Get role ID for template selection
  const roleId = clientType?.id || 'owner';

  // Privileged viewers (owner / admin / CEO / super-admin) bypass module gating:
  // they still see LOCKED sections as upsell teasers so they can enable the
  // module. Everyone else gets locked sections hidden entirely (declutter).
  const userRoles = user?.roles || [];
  const isPrivilegedViewer =
    canSeeTeamView ||
    userRoles.some(r => ['owner', 'admin', 'super_admin', 'ceo', 'coo', 'cfo'].includes(String(r).toLowerCase())) ||
    ['owner', 'admin'].includes(String(user?.role || '').toLowerCase());
  
  // QSR location-filtered metrics
  const qsrMetrics = isQSR ? getQSRDashboardMetrics(currentLocation) : null;
  const qsrKpis = isQSR ? getQSRKpis(currentLocation) : null;
  const qsrLiveMetrics = isQSR ? getQSRLiveMetrics(currentLocation) : null;
  const qsrWins = isQSR ? getQSRWins(currentLocation) : null;
  
  // Effective metrics — QSR overrides when active
  const effectiveMetrics = isQSR ? qsrMetrics : unifiedMetrics;
  const effectiveKpis = isQSR ? qsrKpis : kpis;
  const effectiveLiveMetrics = isQSR ? qsrLiveMetrics : liveMetrics;
  const effectiveWins = isQSR ? qsrWins : unifiedWins;
  const effectiveBusinessName = isQSR && currentLocation
    ? `The Daily Grill — ${currentLocation.shortName}`
    : businessName;
  
  // Get industry-specific template
  const dashboardTemplate = useMemo(() => 
    getDashboardTemplate(industry, roleId, canSeeTeamView),
    [industry, roleId, canSeeTeamView]
  );
  
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  
  // Section order and visibility state - initialized from API
  const [sectionOrder, setSectionOrder] = useState(() => {
    // Start from the industry template order, but append any DEFAULT_SECTIONS the
    // template omits (e.g. 'kpis') so the real KPI Dashboard + benchmarks always
    // render rather than being silently dropped for some industries.
    const base = dashboardTemplate.order || DEFAULT_SECTIONS.map(s => s.id);
    const missing = DEFAULT_SECTIONS.map(s => s.id).filter(id => !base.includes(id));
    return [...base, ...missing];
  });
  
  const [hiddenSections, setHiddenSections] = useState(
    dashboardTemplate.hidden || []
  );

  // Per-user collapsed sections (header-only). Persisted via the same
  // /api/dashboard/preferences document as order + hidden.
  const [collapsedSections, setCollapsedSections] = useState([]);

  // Fetch preferences from API on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/dashboard/preferences`, { credentials: 'include', 
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.preferences?.section_order?.length > 0) {
            // Merge saved preferences with any new sections from DEFAULT_SECTIONS
            const savedOrder = data.preferences.section_order;
            const allSectionIds = DEFAULT_SECTIONS.map(s => s.id);
            const newSections = allSectionIds.filter(id => !savedOrder.includes(id));
            
            // Add new sections after 'wins' section (good default position)
            if (newSections.length > 0) {
              const winsIndex = savedOrder.indexOf('wins');
              const insertIndex = winsIndex !== -1 ? winsIndex + 1 : 3;
              const mergedOrder = [...savedOrder.slice(0, insertIndex), ...newSections, ...savedOrder.slice(insertIndex)];
              setSectionOrder(mergedOrder);
            } else {
              setSectionOrder(savedOrder);
            }
          }
          if (data.preferences?.hidden_sections) {
            setHiddenSections(data.preferences.hidden_sections);
          }
          if (Array.isArray(data.preferences?.collapsed_sections)) {
            // In demo/preview mode always render every section expanded — a
            // client demo must never open with key sections collapsed/hidden.
            setCollapsedSections(isDemoAccount ? [] : data.preferences.collapsed_sections);
          }
        }
      } catch (error) {
        logIfReal('Failed to fetch dashboard preferences', error);
        // Fall back to template defaults - already set
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPreferences();
  }, [user?.id]);

  // Save preferences to API (debounced)
  const savePreferences = useCallback(async (newOrder, newHidden, newCollapsed) => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce API calls
    saveTimeoutRef.current = setTimeout(async () => {
      if (!user) return;
      // Iter-318 — auth via HttpOnly cookie (credentials: 'include'
      // on the fetch below). No localStorage token gate.
      setIsSaving(true);
      try {
        const body = {
          section_order: newOrder,
          hidden_sections: newHidden
        };
        if (newCollapsed !== undefined) body.collapsed_sections = newCollapsed;
        const response = await authFetch(`${API_URL}/api/dashboard/preferences`, { credentials: 'include', 
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        
        if (!response.ok) {
          console.error('Failed to save preferences');
        }
      } catch (error) {
        console.error('Error saving preferences:', error);
      } finally {
        setIsSaving(false);
      }
    }, 500); // 500ms debounce
  // eslint-disable-next-line
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setSectionOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);
        // Save to API
        savePreferences(newOrder, hiddenSections, collapsedSections);
        return newOrder;
      });
    }
  };

  // Toggle section visibility
  const toggleSectionVisibility = (sectionId) => {
    setHiddenSections(prev => {
      const newHidden = prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId];
      // Save to API
      savePreferences(sectionOrder, newHidden, collapsedSections);
      return newHidden;
    });
  };

  // Toggle section collapse (header-only). Persists per-user.
  const toggleSectionCollapse = useCallback((sectionId) => {
    setCollapsedSections(prev => {
      const newCollapsed = prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId];
      savePreferences(sectionOrder, hiddenSections, newCollapsed);
      return newCollapsed;
    });
  }, [sectionOrder, hiddenSections, savePreferences]);

  // Reset to industry template default
  const resetToDefault = async () => {
    const newOrder = dashboardTemplate.order || DEFAULT_SECTIONS.map(s => s.id);
    const newHidden = dashboardTemplate.hidden || [];
    setSectionOrder(newOrder);
    setHiddenSections(newHidden);
    setCollapsedSections([]);
    
    // Save reset to API (auth via cookie)
    if (user) {
      try {
        await authFetch(`${API_URL}/api/dashboard/preferences/reset`, { credentials: 'include', 
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        toast.success('Dashboard reset to default');
      } catch (error) {
        console.error('Failed to reset preferences:', error);
      }
    }
  };
  
  // Apply a specific industry template
  const applyTemplate = async (templateId) => {
    const template = INDUSTRY_TEMPLATES[templateId] || INDUSTRY_TEMPLATES.default;
    setSectionOrder(template.order);
    setHiddenSections(template.hidden || []);
    setShowTemplateSelector(false);
    
    // Save template to API (auth via cookie)
    if (user) {
      try {
        await authFetch(`${API_URL}/api/dashboard/preferences/apply-template/${templateId}`, { credentials: 'include', 
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        toast.success(`Applied ${template.name} template`);
      } catch (error) {
        console.error('Failed to apply template:', error);
      }
    }
  };

  // True when the account has genuine connected metrics flowing in. Used to
  // decide whether data-bearing sections may render real figures; real accounts
  // with no live feed are gated to a "connect a source" prompt (never fake data).
  const hasLiveData = !!(
    effectiveMetrics && (
      effectiveMetrics.revenue?.current || effectiveMetrics.revenue_mtd ||
      effectiveMetrics.customers?.current || effectiveMetrics.active_customers ||
      effectiveMetrics.gross_margin || effectiveMetrics.net_profit
    )
  );

  // Build metrics for pattern analysis. Demo accounts use rich sample numbers;
  // real accounts use their actual metrics (0 when no source is connected) so
  // the hero never fabricates growth figures.
  const patternMetrics = {
    revenue: effectiveMetrics?.revenue?.current ?? effectiveMetrics?.revenue_mtd ?? (isDemoAccount ? 125000 : 0),
    revenue_previous: effectiveMetrics?.revenue?.previous ?? (isDemoAccount ? 115000 : 0),
    customers: effectiveMetrics?.customers?.current ?? effectiveMetrics?.active_customers ?? (isDemoAccount ? 3200 : 0),
    customers_previous: effectiveMetrics?.customers?.previous ?? (isDemoAccount ? 2900 : 0),
    avg_transaction: effectiveMetrics?.avg_transaction ?? (isDemoAccount ? 85 : 0),
    revenue_change: effectiveMetrics?.revenue_change_pct ?? (isDemoAccount ? 8.7 : 0),
    revenue_change_pct: effectiveMetrics?.revenue_change_pct ?? (isDemoAccount ? 8.7 : 0),
    customer_change: effectiveMetrics?.customer_change_pct ?? (isDemoAccount ? 10.3 : 0),
    customer_change_pct: effectiveMetrics?.customer_change_pct ?? (isDemoAccount ? 10.3 : 0)
  };

  // Short "what's inside / what changed" teaser shown on a section's collapsed
  // bar, so owners can tell at a glance whether it's worth re-opening.
  const fmtPct = (v) => `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`;
  const getSectionSummary = (sectionId) => {
    switch (sectionId) {
      case 'insights':
        return highPriorityCount > 0
          ? `${highPriorityCount} priorit${highPriorityCount === 1 ? 'y' : 'ies'} flagged today`
          : 'AI synopsis & today’s focus';
      case 'wins': {
        const n = effectiveWins?.length || 0;
        return n ? `${n} recent win${n === 1 ? '' : 's'} to celebrate` : 'No new wins yet';
      }
      case 'opportunities': {
        const n = unifiedOpportunities?.length || 0;
        return n ? `${n} opportunit${n === 1 ? 'y' : 'ies'} identified` : 'Scanning for opportunities';
      }
      case 'kpis': {
        const n = effectiveKpis?.length || 0;
        return n ? `${n} KPI${n === 1 ? '' : 's'} tracked` : 'Key performance metrics';
      }
      case 'financial': {
        const ch = patternMetrics.revenue_change_pct;
        return Number.isFinite(ch) && ch !== 0
          ? `Revenue ${fmtPct(ch)} vs last period`
          : 'Revenue, cash flow & P&L';
      }
      case 'tasks':
        return `${pendingCount || 0} pending · ${completedCount || 0} done`;
      case 'concerns':
        return 'Risks & issues to review';
      case 'performance':
        return 'Staff & customer performance';
      case 'market':
        return 'Competitor & supplier intel';
      case 'planning':
        return 'Goals, weather & forecasts';
      case 'live-metrics':
        return 'Real-time business pulse';
      case 'connected-sources':
        return 'Your linked data sources';
      case 'economic-pulse':
        return 'AU economic indicators';
      case 'seasonal':
        return 'Seasonal demand outlook';
      case 'benchmark-alerts':
        return 'Industry benchmark alerts';
      default:
        return null;
    }
  };

  // Section renderer
  const renderSection = (sectionId) => {
    const section = DEFAULT_SECTIONS.find(s => s.id === sectionId);
    if (!section) return null;
    const isVisible = !hiddenSections.includes(sectionId);

    // Module gating: check if this section is unlocked by active modules.
    const sectionUnlocked = isSectionUnlocked(sectionId, activeModules);
    if (!sectionUnlocked) {
      // QSR demo: every module is "enabled" — never show upsell teasers, just
      // hide locked generic sections so the demo stays clean.
      if (isDemoAccount && industry === 'qsr') return null;
      // Owner/Admin/CEO still see a teaser (upsell to the decision-maker).
      // Non-privileged staff get the locked section hidden entirely so the
      // dashboard only shows modules that are actually enabled for them.
      if (isPrivilegedViewer) {
        const teaser = getTeaserForSection(sectionId);
        if (teaser) {
          return (
            <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
              <ModuleTeaser teaser={teaser} onEnableModule={toggleModule} />
            </SortableSection>
          );
        }
      }
      return null;
    }

    // Connected sources — prefer the real connected list from the metrics API,
    // fall back to the user's integration array, then demo defaults (demo only).
    const connectedIntegrations = (Array.isArray(dataSources) && dataSources.length > 0)
      ? dataSources
      : (user?.integrations?.filter(i => i.status === 'connected')?.map(i => i.provider))
        || (isDemoAccount ? ['xero', 'square', 'google_analytics'] : []);

    switch (sectionId) {
      case 'connected-sources':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <ConnectedSourcesStrip 
              connectedIntegrations={connectedIntegrations}
              governmentSources={['ato', 'abs', 'rba']}
              industry={industry}
              lastSync={unifiedMetrics?.last_sync}
              onManageIntegrations={() => navigate('/app/integrations')}
            />
          </SortableSection>
        );
      case 'module-control':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <ModuleTogglePanel />
          </SortableSection>
        );
      case 'insights':
        // QSR demo: the ruby hero + RoleHome already deliver the greeting and
        // QSR-specific glance metrics, so skip the generic greeting/metrics hero
        // to avoid a duplicate "Good morning" and off-brand revenue/traffic tiles.
        if (isDemoAccount && industry === 'qsr') return null;
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <AIIntelligenceHero
              userName={userName}
              businessId={businessId || 'demo'}
              businessName={effectiveBusinessName}
              industry={industry}
              metrics={patternMetrics}
              connectedSources={connectedIntegrations}
              lastLogin={lastLogin}
              currentLocation={currentLocation}
              kpis={effectiveKpis}
              wins={effectiveWins}
              completedCount={completedCount}
              pendingCount={pendingCount}
              highPriorityCount={highPriorityCount}
              onSwitchTab={setActiveTab}
              onViewInsights={(insight) => navigate('/app/management')}
              onViewRecommendation={(rec) => toast.info(`Opening: ${rec.title}`)}
              showGreeting={false}
              isDemo={isDemoAccount}
            />
          </SortableSection>
        );
      case 'seasonal':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <SeasonalOutlookBanner industry={industry} />
          </SortableSection>
        );
      case 'splh-heatmap':
        if (industry !== 'qsr') return null;
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <SPLHComparisonDashboard />
          </SortableSection>
        );
      case 'location-comparison':
        if (industry !== 'qsr') return null;
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <LocationComparisonMode />
          </SortableSection>
        );
      case 'wins':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <WinsSection isEnabled={isWidgetEnabled('recent-wins')} unifiedWins={effectiveWins} industry={industry} connectedSources={connectedIntegrations} isDemoAccount={isDemoAccount} />
          </SortableSection>
        );
      case 'economic-pulse':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <EconomicIndicatorsWidget industry={industry} />
          </SortableSection>
        );
      case 'concerns':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <ConcernsSection isEnabled={isWidgetEnabled('business-concerns')} canSeeTeamView={canSeeTeamView} industry={industry} isDemoAccount={isDemoAccount} />
          </SortableSection>
        );
      case 'benchmark-alerts':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <DemoDataGate show={isDemoAccount || hasLiveData} title="Benchmark Alerts" description="Connect your accounting data to see how your margins compare to verified industry benchmarks.">
              <BenchmarkAlertsSection 
                isEnabled={true} 
                canSeeTeamView={canSeeTeamView} 
                industry={industry}
                unifiedMetrics={effectiveMetrics}
              />
            </DemoDataGate>
          </SortableSection>
        );
      case 'live-benchmarks':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <Card className="border border-dashed border-border/60 bg-muted/20">
              <CardContent className="flex items-center justify-between py-5 px-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Live Industry Benchmarks</p>
                    <p className="text-xs text-muted-foreground">Available in Market Intel module</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate('/app/market-intelligence')}>
                  Open Market Intel
                </Button>
              </CardContent>
            </Card>
          </SortableSection>
        );
      case 'opportunities':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <OpportunitiesSection isEnabled={isWidgetEnabled('opportunities')} unifiedOpportunities={unifiedOpportunities} industry={industry} currentLocation={currentLocation} />
          </SortableSection>
        );
      case 'financial':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <DemoDataGate show={isDemoAccount || hasLiveData} title="Financial Intelligence" description="Connect Xero, MYOB or your POS to surface real revenue forecasts and P&L benchmarks.">
              <FinancialSection
                canSeeTeamView={canSeeTeamView}
                isRevenueForecastEnabled={isWidgetEnabled('revenue-forecast')}
                isPLEnabled={isWidgetEnabled('pl-snapshot')}
                unifiedMetrics={effectiveMetrics}
                industry={industry}
                dataSources={connectedIntegrations}
              />
            </DemoDataGate>
          </SortableSection>
        );
      case 'performance':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <PerformanceSection
              canSeeTeamView={canSeeTeamView}
              isStaffEnabled={isWidgetEnabled('staff-performance')}
              isSentimentEnabled={isWidgetEnabled('customer-sentiment')}
              industry={industry}
              onExpandCard={onExpandCard}
              isDemo={isDemoAccount}
            />
          </SortableSection>
        );
      case 'market':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <MarketSection
              canSeeTeamView={canSeeTeamView}
              isCompetitorEnabled={isWidgetEnabled('competitor-intelligence')}
              isSupplierEnabled={isWidgetEnabled('supplier-performance')}
              industry={industry}
              onExpandCard={onExpandCard}
              isDemo={isDemoAccount}
            />
          </SortableSection>
        );
      case 'planning':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <PlanningSection
              canSeeTeamView={canSeeTeamView}
              isWeatherEnabled={isWidgetEnabled('weather-impact')}
              isGoalEnabled={isWidgetEnabled('goal-tracker')}
              industry={industry}
              onExpandCard={onExpandCard}
            />
          </SortableSection>
        );
      case 'kpis':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <KPISection kpis={effectiveKpis} industry={industry} dataSources={isQSR ? ['summit_qsr', 'square'] : connectedIntegrations} />
          </SortableSection>
        );
      case 'live-metrics':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <LiveMetricsBar liveMetrics={effectiveLiveMetrics} />
          </SortableSection>
        );
      case 'tasks':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <TasksRecommendationsRow
              tasks={tasks}
              recommendations={recommendations}
              onToggleTask={onToggleTask}
              onExpandTask={onExpandTask}
              setActiveTab={setActiveTab}
            />
          </SortableSection>
        );
      case 'upsell':
        if (isDemoAccount && industry === 'qsr') return null;
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <UpsellTeasers
              enabledModules={activeModules}
              industry={industry}
              industryData={industryData}
              onExploreModule={(moduleId) => navigate(`/pricing?highlight=${moduleId}`)}
            />
          </SortableSection>
        );
      case 'quick-stats':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <QuickStatsGrid
              completedCount={completedCount}
              pendingCount={pendingCount}
              highPriorityCount={highPriorityCount}
              kpis={kpis}
              setActiveTab={setActiveTab}
              setFilter={setFilter}
            />
          </SortableSection>
        );
      case 'activity':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <DemoDataGate show={isDemoAccount} title="Recent Activity">
              <RecentActivitySection industry={industry} currentLocation={currentLocation} isDemoAccount={isDemoAccount} />
            </DemoDataGate>
          </SortableSection>
        );
      case 'quick-links':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <QuickLinksSection industry={industry} />
          </SortableSection>
        );
      case 'ai-narrative':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <DemoDataGate show={isDemoAccount} title="Daily Intelligence Briefing">
              <AINarrativeEngine industry={industry} currentLocation={currentLocation} />
            </DemoDataGate>
          </SortableSection>
        );
      case 'predictive-alerts':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <DemoDataGate show={isDemoAccount} title="Predictive Alerts">
              <PredictiveAnomalyDetection industry={industry} />
            </DemoDataGate>
          </SortableSection>
        );
      case 'knowledge-transfer':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <KnowledgeTransferEngine industry={industry} />
          </SortableSection>
        );
      case 'staff-roi':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <DemoDataGate show={isDemoAccount} title="Staff Revenue Impact">
              <StaffROIScoring industry={industry} />
            </DemoDataGate>
          </SortableSection>
        );
      case 'what-if':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <WhatIfScenarioModeler industry={industry} />
          </SortableSection>
        );
      case 'revenue-leakage':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <DemoDataGate show={isDemoAccount} title="Revenue Leakage">
              <RevenueLeakageDetector industry={industry} />
            </DemoDataGate>
          </SortableSection>
        );
      case 'executive-summary':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <DemoDataGate show={isDemoAccount} title="Executive Summary">
              <ExecutiveSummaryGenerator industry={industry} />
            </DemoDataGate>
          </SortableSection>
        );
      case 'cx-scorecard':
        return (
          <SortableSection key={sectionId} id={sectionId} isEditMode={isEditMode} section={section} onToggleVisibility={toggleSectionVisibility} isVisible={isVisible}>
            <DemoDataGate show={isDemoAccount} title="Customer Experience">
              <CustomerExperienceScorecard industry={industry} />
            </DemoDataGate>
          </SortableSection>
        );
      default:
        return null;
    }
  };

  return (
    <SectionCollapseContext.Provider value={{ collapsedList: collapsedSections, toggle: toggleSectionCollapse, getSummary: getSectionSummary }}>
    <div className="space-y-4 sm:space-y-6">
      {/* Dashboard Header with Template Info */}
      {canSeeTeamView && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {!isEditMode && dashboardTemplate && (
              <Badge variant="outline" className="text-xs bg-white" data-testid="template-badge">
                <Layers className="w-3 h-3 mr-1" />
                {dashboardTemplate.name} Layout
                <span className="ml-1 text-slate-400">(Auto-applied)</span>
              </Badge>
            )}
            {isEditMode && (
              <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200">
                <LayoutGrid className="w-3 h-3 mr-1" />
                Customize Mode
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditMode && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  <Layers className="w-3.5 h-3.5 mr-1" />
                  Change Template
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetToDefault}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Reset
                </Button>
              </>
            )}
            <Button
              variant={isEditMode ? "default" : "outline"}
              size="sm"
              onClick={() => setIsEditMode(!isEditMode)}
              className={isEditMode ? "bg-indigo-600 hover:bg-indigo-700" : ""}
              data-testid="customize-dashboard-btn-inline"
            >
              {isEditMode ? (
                <>
                  {isSaving ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5 mr-1" />
                  )}
                  {isSaving ? 'Saving...' : 'Done'}
                </>
              ) : (
                <>
                  <Settings className="w-3.5 h-3.5 mr-1" />
                  Customize Dashboard
                </>
              )}
            </Button>
          </div>
        </div>
      )}
      
      {/* Industry Template Selector */}
      {isEditMode && showTemplateSelector && (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="p-4">
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-600" />
              Choose an Industry Template
            </h4>
            <p className="text-xs text-slate-600 mb-4">
              Apply a pre-configured layout optimized for your industry. You can still customize after applying.
            </p>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(INDUSTRY_TEMPLATES).map(([id, template]) => (
                <Button
                  key={id}
                  variant="outline"
                  size="sm"
                  onClick={() => applyTemplate(id)}
                  className={`h-auto py-2 px-3 flex-col items-start text-left ${
                    dashboardTemplate.name === template.name ? 'border-purple-400 bg-purple-50' : ''
                  }`}
                >
                  <span className="font-medium text-xs">{template.name}</span>
                  <span className="text-[10px] text-slate-500 line-clamp-1">{template.focusAreas?.[0]}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Mode Instructions */}
      {isEditMode && !showTemplateSelector && (
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <LayoutGrid className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Customize Your Dashboard</h4>
                <p className="text-sm text-slate-600">
                  Drag sections to reorder. Hover over a section to show/hide it. 
                  <span className="text-indigo-600 font-medium"> Your layout syncs to the cloud automatically.</span>
                </p>
              </div>
            </div>
            
            {/* Section Legend */}
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs text-slate-500 mr-2">Priority:</span>
              {Object.entries(PRIORITY_COLORS).map(([priority, className]) => (
                <Badge key={priority} className={`text-[10px] ${className}`}>
                  {priority}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Live Industry Benchmarks - Relocated to Market Intel */}

      {/* Draggable Sections */}
      {isEditMode ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
            {sectionOrder.map(sectionId => renderSection(sectionId))}
          </SortableContext>
        </DndContext>
      ) : (
        <div className="flex flex-col gap-4 sm:gap-6">
          {sectionOrder.map(sectionId => {
            const node = renderSection(sectionId);
            if (!node) return null;
            return <div key={`col-${sectionId}`}>{node}</div>;
          })}
        </div>
      )}

      {/* AI Savings Finder — gated by the 'ai-savings-finder' dashboard widget toggle */}
      {isWidgetEnabled('ai-savings-finder') && !(isDemoAccount && industry === 'qsr') && (
        <div className="mb-4 sm:mb-6" data-testid="savings-finder-slot"><AISavingsFinderTile /></div>
      )}

      {/* Credits Widget - Always at bottom, not draggable (hidden in QSR demo) */}
      {!(isDemoAccount && industry === 'qsr') && (
        <CreditsWidget
          compact={true}
          onTopUp={() => navigate('/app/account')}
          data-testid="dashboard-credits-widget"
        />
      )}
    </div>
    </SectionCollapseContext.Provider>
  );
};

export default DraggableOverviewTab;
